import {
  collection, query, orderBy, limit, getDocs, deleteDoc, doc,
  addDoc, serverTimestamp, where, writeBatch,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase/app';
import type { PublicMessage } from '../types';

const COL = 'messages';
const CACHE_KEY = 'admin_msg_cache';
const CACHE_TS_KEY = 'admin_msg_cache_ts';

// ─── Cache helpers ───

interface CachedMsg {
  id: string;
  text: string;
  senderId: string;
  senderNickname: string;
  gender: 'male' | 'female' | 'unknown';
  profileImageUrl?: string;
  lat: number;
  lng: number;
  geohash?: string;
  radius: number;
  isBot: boolean;
  isNearbyNotification: boolean;
  manualFreqApplied: boolean;
  timestampMs: number;
}

function loadCache(): CachedMsg[] {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveCache(msgs: CachedMsg[]) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(msgs));
    localStorage.setItem(CACHE_TS_KEY, String(Date.now()));
  } catch { /* storage full */ }
}

function msgToCache(m: PublicMessage): CachedMsg {
  return {
    id: m.id, text: m.text, senderId: m.senderId,
    senderNickname: m.senderNickname, gender: m.gender,
    profileImageUrl: m.profileImageUrl,
    lat: m.lat, lng: m.lng, geohash: m.geohash,
    radius: m.radius, isBot: m.isBot,
    isNearbyNotification: m.isNearbyNotification,
    manualFreqApplied: m.manualFreqApplied,
    timestampMs: m.timestamp?.toMillis?.() ?? 0,
  };
}

function cacheToMsg(c: CachedMsg): PublicMessage {
  return {
    ...c,
    timestamp: Timestamp.fromMillis(c.timestampMs),
  } as PublicMessage;
}

export function getCacheTimestamp(): number {
  return parseInt(localStorage.getItem(CACHE_TS_KEY) || '0') || 0;
}

export function clearMessageCache() {
  localStorage.removeItem(CACHE_KEY);
  localStorage.removeItem(CACHE_TS_KEY);
}

/** Merge new messages into cache, remove deleted IDs, return full sorted list */
function mergeIntoCache(newMsgs: PublicMessage[], deletedIds?: Set<string>): PublicMessage[] {
  const cached = loadCache();
  const map = new Map<string, CachedMsg>();

  // Load existing cache
  for (const c of cached) {
    if (deletedIds && deletedIds.has(c.id)) continue;
    map.set(c.id, c);
  }

  // Add/overwrite with new messages
  for (const m of newMsgs) {
    map.set(m.id, msgToCache(m));
  }

  const sorted = [...map.values()].sort((a, b) => a.timestampMs - b.timestampMs);
  saveCache(sorted);
  return sorted.map(cacheToMsg);
}

/** Remove specific IDs from cache */
function removeFromCache(ids: string[]) {
  const cached = loadCache();
  const idSet = new Set(ids);
  const filtered = cached.filter(c => !idSet.has(c.id));
  saveCache(filtered);
}

// ─── Public API ───

/** Get all cached messages (no server call) */
export function getCachedMessages(): PublicMessage[] {
  return loadCache().map(cacheToMsg);
}

/** Fetch recent messages from server, merge into cache */
export async function fetchRecentMessages(msgLimit = 500): Promise<PublicMessage[]> {
  const q = query(collection(db, COL), orderBy('timestamp', 'desc'), limit(msgLimit));
  const snap = await getDocs(q);
  const msgs = snap.docs.map(d => ({ id: d.id, ...d.data() } as PublicMessage));
  return mergeIntoCache(msgs);
}

/** Fetch messages by date range — replaces cache with only the fetched results */
export async function fetchMessagesByDateRange(startDate: Date, endDate: Date): Promise<PublicMessage[]> {
  const q = query(
    collection(db, COL),
    where('timestamp', '>=', Timestamp.fromDate(startDate)),
    where('timestamp', '<=', Timestamp.fromDate(endDate)),
    orderBy('timestamp', 'desc'),
    limit(5000),
  );
  const snap = await getDocs(q);
  const msgs = snap.docs.map(d => ({ id: d.id, ...d.data() } as PublicMessage));
  // Replace cache entirely so the list shows only the requested range
  const cached = msgs.map(msgToCache).sort((a, b) => a.timestampMs - b.timestampMs);
  saveCache(cached);
  return cached.map(cacheToMsg);
}

/** Full reload: clear cache and re-fetch */
export async function fullReloadMessages(msgLimit = 2000): Promise<PublicMessage[]> {
  clearMessageCache();
  return fetchRecentMessages(msgLimit);
}

/** Send a message */
export async function sendMessage(params: {
  text: string;
  senderId: string;
  senderNickname: string;
  gender: 'male' | 'female' | 'unknown';
  lat: number;
  lng: number;
  radius: number;
  isBot?: boolean;
}): Promise<string> {
  const ref = await addDoc(collection(db, COL), {
    text: params.text,
    senderId: params.senderId,
    senderNickname: params.senderNickname,
    gender: params.gender,
    profileImageUrl: null,
    lat: params.lat,
    lng: params.lng,
    radius: params.radius,
    isBot: params.isBot ?? false,
    isNearbyNotification: false,
    manualFreqApplied: false,
    timestamp: serverTimestamp(),
  });
  return ref.id;
}

/** Delete a message and remove from cache */
export async function deleteMessage(messageId: string): Promise<void> {
  await deleteDoc(doc(db, COL, messageId));
  removeFromCache([messageId]);
}

/** Delete multiple messages and remove from cache */
export async function deleteMultipleMessages(ids: string[]): Promise<void> {
  const batch = writeBatch(db);
  ids.forEach(id => batch.delete(doc(db, COL, id)));
  await batch.commit();
  removeFromCache(ids);
}

/** Delete all messages */
export async function deleteAllMessages(): Promise<number> {
  const snap = await getDocs(query(collection(db, COL), limit(500)));
  const batch = writeBatch(db);
  snap.docs.forEach(d => batch.delete(d.ref));
  await batch.commit();
  clearMessageCache();
  return snap.size;
}

/** Delete all bot messages */
export async function deleteAllBotMessages(): Promise<number> {
  const q = query(collection(db, COL), where('isBot', '==', true), limit(500));
  const snap = await getDocs(q);
  const ids = snap.docs.map(d => d.id);
  const batch = writeBatch(db);
  snap.docs.forEach(d => batch.delete(d.ref));
  await batch.commit();
  removeFromCache(ids);
  return snap.size;
}
